// myapp/src/App.tsx
import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Import Helmet Provider for dynamic SEO metadata
import { HelmetProvider } from 'react-helmet-async';
import Analytics from './components/common/Analytics';

// Import Auth Providers
import { AdminAuthProvider } from './context/AdminAuthContext';
import { AppAuthProvider } from './context/AppAuthContext';

// Import security guards
import { AdminPortalGuard, AppPortalGuard } from './components/common/PortalGuard';
import { AdminProtectedRoute, AppProtectedRoute } from './components/common/ProtectedRoute';

// Lazy load isolated layouts
const AdminLayout = React.lazy(() => import('./layouts/AdminLayout'));
const AppLayout = React.lazy(() => import('./layouts/AppLayout'));

// Lazy load public marketing views
const Home = React.lazy(() => import('./pages/Home'));
const Login = React.lazy(() => import('./pages/Login'));
const AdminLogin = React.lazy(() => import('./pages/admin/Login'));
const ModuleDetails = React.lazy(() => import('./pages/public/ModuleDetails'));

// Lazy load public information views and layout
const PublicPageLayout = React.lazy(() => import('./layouts/PublicPageLayout'));
const AboutUs = React.lazy(() => import('./pages/public/About'));
const PrivacyPolicy = React.lazy(() => import('./pages/public/Privacy'));
const TermsOfService = React.lazy(() => import('./pages/public/Terms'));
const SecurityDetails = React.lazy(() => import('./pages/public/Security'));
const ContactUs = React.lazy(() => import('./pages/public/Contact'));

// Lazy load new SEO-ready landing pages
const Features = React.lazy(() => import('./pages/public/Features'));
const Pricing = React.lazy(() => import('./pages/public/Pricing'));
const ErpSoftwareLanding = React.lazy(() => import('./pages/public/features/ErpSoftware'));
const HrmSoftwareLanding = React.lazy(() => import('./pages/public/features/HrmSoftware'));
const PayrollManagementLanding = React.lazy(() => import('./pages/public/features/PayrollManagement'));
const AttendanceManagementLanding = React.lazy(() => import('./pages/public/features/AttendanceManagement'));
const EmployeeManagementLanding = React.lazy(() => import('./pages/public/features/EmployeeManagement'));
const CrmSoftwareLanding = React.lazy(() => import('./pages/public/features/CrmSoftware'));
const InventoryManagementLanding = React.lazy(() => import('./pages/public/features/InventoryManagement'));
const InvoiceManagementLanding = React.lazy(() => import('./pages/public/features/InvoiceManagement'));
const DynamicIndustryPage = React.lazy(() => import('./pages/public/industries/DynamicIndustryPage'));
const DynamicSolutionPage = React.lazy(() => import('./pages/public/solutions/DynamicSolutionPage'));
const BlogList = React.lazy(() => import('./pages/public/blog/BlogList'));
const BlogDetails = React.lazy(() => import('./pages/public/blog/BlogDetails'));

// Lazy load SuperAdmin portal dashboards
const AdminDashboard = React.lazy(() => import('./pages/admin/Dashboard'));
const CompanyManagement = React.lazy(() => import('./pages/admin/CompanyManagement'));
const SystemAudits = React.lazy(() => import('./pages/admin/SystemAudits'));
const ContactInquiries = React.lazy(() => import('./pages/admin/ContactInquiries'));

// Lazy load SuperAdmin Subscription Management pages
const SubscriptionPlans = React.lazy(() => import('./pages/admin/SubscriptionPlans'));
const TenantSubscriptions = React.lazy(() => import('./pages/admin/TenantSubscriptions'));
const PaymentLedger = React.lazy(() => import('./pages/admin/PaymentLedger'));

// Lazy load Employee/Tenant portal dashboards & pages
const CustomerDashboard = React.lazy(() => import('./pages/customer/Dashboard'));
const BillingDashboard = React.lazy(() => import('./pages/customer/BillingDashboard'));
const FileManagement = React.lazy(() => import('./pages/customer/FileManagement'));
const EmployeeManagement = React.lazy(() => import('./pages/employee/EmployeeManagement'));
const DepartmentManagement = React.lazy(() => import('./pages/employee/DepartmentManagement'));
const LeaveRequests = React.lazy(() => import('./pages/employee/LeaveRequests'));
const AttendanceLogs = React.lazy(() => import('./pages/employee/AttendanceLogs'));
const InventoryCatalog = React.lazy(() => import('./pages/employee/InventoryCatalog'));
const UserProfile = React.lazy(() => import('./pages/employee/UserProfile'));
const RoleManagement = React.lazy(() => import('./pages/employee/RoleManagement'));
const SalaryManagement = React.lazy(() => import('./pages/employee/SalaryManagement'));
const EmployeeSalaryPortal = React.lazy(() => import('./pages/employee/EmployeeSalaryPortal'));

// Lazy load Invoice & Billing views
const InvoiceDashboard = React.lazy(() => import('./pages/invoice/InvoiceDashboard'));
const CustomerManagement = React.lazy(() => import('./pages/invoice/CustomerManagement'));
const InvoiceManagement = React.lazy(() => import('./pages/invoice/InvoiceManagement'));
const InvoiceDetails = React.lazy(() => import('./pages/invoice/InvoiceDetails'));
const InvoiceReports = React.lazy(() => import('./pages/invoice/InvoiceReports'));

// Lazy load secure status validation screens
const Unauthorized = React.lazy(() => import('./pages/status/Unauthorized'));
const SessionExpired = React.lazy(() => import('./pages/status/SessionExpired'));
const PortalMismatch = React.lazy(() => import('./pages/status/PortalMismatch'));

// Reusable spinner component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-950">
    <div className="w-8 h-8 border-2 border-indigo-650 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Determine active subdomain dynamically
const getSubdomain = (): 'admin' | 'app' | 'public' => {
  const hostname = window.location.hostname;
  if (hostname.startsWith('admin.')) return 'admin';
  if (hostname.startsWith('app.')) return 'app';
  return 'public';
};

const App = () => {
  const subdomain = getSubdomain();

  return (
    <HelmetProvider>
      <Analytics />
      <BrowserRouter>
        <ToastContainer 
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
        />
        <Suspense fallback={<LoadingSpinner />}>
          {subdomain === 'admin' ? (
            <AdminAuthProvider>
              <Routes>
                <Route element={<AdminPortalGuard />}>
                  <Route element={<AdminLayout />}>
                    <Route element={<AdminProtectedRoute allowedRoles={['SuperAdmin']} />}>
                      <Route path="/admin/dashboard" element={<AdminDashboard />} />
                      <Route path="/admin/companies" element={<CompanyManagement />} />
                      <Route path="/admin/plans" element={<SubscriptionPlans />} />
                      <Route path="/admin/subscriptions" element={<TenantSubscriptions />} />
                      <Route path="/admin/payments" element={<PaymentLedger />} />
                      <Route path="/admin/logs" element={<SystemAudits />} />
                      <Route path="/admin/inquiries" element={<ContactInquiries />} />
                    </Route>
                  </Route>
                  <Route path="/admin/login" element={<AdminLogin />} />
                </Route>
                
                {/* Common Status Pages */}
                <Route path="/unauthorized" element={<Unauthorized />} />
                <Route path="/session-expired" element={<SessionExpired />} />
                <Route path="/portal-mismatch" element={<PortalMismatch />} />
                
                {/* Fallback to login in Admin Subdomain */}
                <Route path="*" element={<Navigate to="/admin/login" replace />} />
              </Routes>
            </AdminAuthProvider>
          ) : subdomain === 'app' ? (
            <AppAuthProvider>
              <Routes>
                <Route element={<AppPortalGuard />}>
                  <Route element={<AppLayout />}>
                    {/* Outer gate: any authenticated portal user (all defined roles) */}
                    <Route element={<AppProtectedRoute allowedRoles={['CompanyAdmin', 'HRManager', 'Employee', 'InventoryManager', 'DepartmentManager', 'SalesManager', 'Accountant']} />}>
                      {/* Dashboard & Profile: accessible to every authenticated portal user */}
                      <Route path="/app/dashboard" element={<CustomerDashboard />} />
                      <Route path="/app/my-dashboard" element={<CustomerDashboard />} />
                      <Route path="/app/profile" element={<UserProfile />} />

                      {/* Staff & Departments: role OR permission-based (custom roles use permissions) */}
                      <Route element={<AppProtectedRoute allowedRoles={['CompanyAdmin', 'HRManager', 'DepartmentManager']} allowedPermissions={['Employees:View', 'Employees:Manage']} />}>
                        <Route path="/app/employees" element={<EmployeeManagement />} />
                      </Route>

                      <Route element={<AppProtectedRoute allowedRoles={['CompanyAdmin', 'HRManager', 'DepartmentManager']} allowedPermissions={['Departments:View', 'Departments:Manage']} />}>
                        <Route path="/app/departments" element={<DepartmentManagement />} />
                      </Route>

                      <Route element={<AppProtectedRoute allowedRoles={['CompanyAdmin', 'HRManager']} allowedPermissions={['Roles:View', 'Roles:Manage']} />}>
                        <Route path="/app/roles" element={<RoleManagement />} />
                      </Route>

                      {/* Billing & Files: CompanyAdmin only */}
                      <Route element={<AppProtectedRoute allowedRoles={['CompanyAdmin']} />}>
                        <Route path="/app/billing" element={<BillingDashboard />} />
                        <Route path="/app/files" element={<FileManagement />} />
                      </Route>

                      {/* Leave & Attendance: role OR permission-based */}
                      <Route element={<AppProtectedRoute allowedRoles={['CompanyAdmin', 'HRManager', 'DepartmentManager', 'Employee', 'SalesManager', 'Accountant']} allowedPermissions={['Leave:View', 'Leave:Manage']} />}>
                        <Route path="/app/leaves" element={<LeaveRequests />} />
                      </Route>

                      <Route element={<AppProtectedRoute allowedRoles={['CompanyAdmin', 'HRManager', 'DepartmentManager', 'Employee', 'SalesManager', 'Accountant']} allowedPermissions={['Attendance:View', 'Attendance:Manage']} />}>
                        <Route path="/app/attendance" element={<AttendanceLogs />} />
                      </Route>

                      {/* Inventory: role OR permission-based */}
                      <Route element={<AppProtectedRoute allowedRoles={['CompanyAdmin', 'InventoryManager']} allowedPermissions={['Inventory:View', 'Inventory:Manage']} />}>
                        <Route path="/app/inventory" element={<InventoryCatalog />} />
                      </Route>

                      {/* Salary management: role OR permission-based */}
                      <Route element={<AppProtectedRoute allowedRoles={['CompanyAdmin', 'HRManager', 'Accountant']} allowedPermissions={['Salary:View', 'Salary:Manage']} />}>
                        <Route path="/app/salary" element={<SalaryManagement />} />
                      </Route>

                      {/* Own salary slip: any role with Salary:ViewOwn permission */}
                      <Route element={<AppProtectedRoute allowedPermissions={['Salary:ViewOwn']} />}>
                        <Route path="/app/my-salary" element={<EmployeeSalaryPortal />} />
                      </Route>

                      {/* Invoice & Billing management: role OR permission-based */}
                      <Route element={<AppProtectedRoute allowedRoles={['CompanyAdmin', 'Accountant', 'SalesManager']} allowedPermissions={['Invoices:View', 'Invoices:Add', 'Invoices:Edit']} />}>
                        <Route path="/app/invoices/dashboard" element={<InvoiceDashboard />} />
                        <Route path="/app/invoices/customers" element={<CustomerManagement />} />
                        <Route path="/app/invoices" element={<InvoiceManagement />} />
                        <Route path="/app/invoices/:id" element={<InvoiceDetails />} />
                      </Route>

                      <Route element={<AppProtectedRoute allowedRoles={['CompanyAdmin', 'Accountant', 'SalesManager']} allowedPermissions={['Invoices:Export', 'Invoices:View']} />}>
                        <Route path="/app/invoices/reports" element={<InvoiceReports />} />
                      </Route>
                    </Route>
                  </Route>
                  <Route path="/login" element={<Login />} />
                </Route>

                {/* Common Status Pages */}
                <Route path="/unauthorized" element={<Unauthorized />} />
                <Route path="/session-expired" element={<SessionExpired />} />
                <Route path="/portal-mismatch" element={<PortalMismatch />} />

                {/* Fallback to login in Tenant Subdomain */}
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            </AppAuthProvider>
          ) : (
            <Routes>
              <Route path="/" element={<Home />} />

              {/* Public Info Pages wrapped in Layout */}
              <Route element={<PublicPageLayout />}>
                <Route path="/about" element={<AboutUs />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/security" element={<SecurityDetails />} />
                <Route path="/contact" element={<ContactUs />} />
                <Route path="/modules/:moduleId" element={<ModuleDetails />} />

                {/* Core Marketing Landing Pages */}
                <Route path="/features" element={<Features />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/features/erp-software" element={<ErpSoftwareLanding />} />
                <Route path="/features/hrm-software" element={<HrmSoftwareLanding />} />
                <Route path="/features/payroll-management" element={<PayrollManagementLanding />} />
                <Route path="/features/attendance-management" element={<AttendanceManagementLanding />} />
                <Route path="/features/employee-management" element={<EmployeeManagementLanding />} />
                <Route path="/features/crm-software" element={<CrmSoftwareLanding />} />
                <Route path="/features/inventory-management" element={<InventoryManagementLanding />} />
                <Route path="/features/invoice-management" element={<InvoiceManagementLanding />} />
                <Route path="/industries/:industryId" element={<DynamicIndustryPage />} />
                <Route path="/solutions/:solutionId" element={<DynamicSolutionPage />} />
                <Route path="/blog" element={<BlogList />} />
                <Route path="/blog/:slug" element={<BlogDetails />} />
              </Route>
              
              {/* Common Status Pages */}
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="/session-expired" element={<SessionExpired />} />
              <Route path="/portal-mismatch" element={<PortalMismatch />} />

              {/* Fallback to Home in Public Subdomain */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          )}
        </Suspense>
      </BrowserRouter>
    </HelmetProvider>
  );
};

export default App;
